import type React from 'react';
import type { ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import Box from '#/ui/components/box';
import type { OverrideProps } from '#/ui/utils/types';

export interface CardActionsOwnProps {
  children?: React.ReactNode;
  className?: string;
  /**
   * If true, the actions will be placed at the start of the card.
   * @default false
   */
  disableSpacing?: boolean;
}

export type CardActionsTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'div',
> = {
  props: AdditionalProps & CardActionsOwnProps;
  defaultComponent: RootComponent;
};

export type CardActionsProps<
  RootComponent extends React.ElementType = CardActionsTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<CardActionsTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function CardActions<C extends ElementType = 'div'>(
  { component = 'div', children, className, disableSpacing = false, ...props }: CardActionsProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const actionClasses = twMerge(
    'gooey-card-actions px-3 pb-3',
    'd-card-actions',
    !disableSpacing && 'justify-end gap-2',
    className
  );

  return (
    <Box component={component} ref={ref} className={actionClasses} {...props}>
      {children}
    </Box>
  );
}
CardActions.displayName = 'CardActions';

export default CardActions;
